'use client';

import { useTranslations } from 'next-intl';
import { ComponentPropsWithoutRef } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { getPriceValueTranslation } from '@/entities/cart';
import { cn } from '@/shared/lib/cn';
import { useSelectedCurrency } from '@/shared/lib/hooks/useSelectedCurrency';
import { Button } from '@/shared/ui/button';
import { Card } from '@/shared/ui/card';
import { Form } from '@/shared/ui/form';
import './cart-bonus-points-payment-box.scss';

const MAX = 150;

type Props = ComponentPropsWithoutRef<typeof Card>;
export const CartBonusPointsPaymentBox = ({ className, ...props }: Props) => {
    const t = useTranslations();
    const [selectedCurrency] = useSelectedCurrency();
    const formMethods = useFormContext();
    const [points] = useWatch({
        control: formMethods.control,
        name: 'bonusPoints',
    });
    return (
        <Card {...props} className={cn('cart-bonus-points-payment-box', className)}>
            <h3 className="cart-bonus-points-payment-box__title" data-testid="bonus-points-title">
                {t('CART_BONUS_POINTS_PAYMENT_BOX.TITLE')}
            </h3>
            <p data-testid="bonus-points-description">
                {t.rich('CART_BONUS_POINTS_PAYMENT_BOX.DESCRIPTION', {
                    max: MAX,
                    maxPrice: getPriceValueTranslation({ t, amount: MAX, currency: selectedCurrency }),
                    b: (chunks) => <b>{chunks}</b>,
                })}
            </p>
            <div>
                <div className="cart-bonus-points-payment-box__points-info">
                    <b className="cart-bonus-points-payment-box__points-label" data-testid="points-applied">
                        {t('CART_BONUS_POINTS_PAYMENT_BOX.APPLY_POINTS', {
                            points: getPriceValueTranslation({
                                t,
                                amount: points,
                                currency: selectedCurrency,
                            }),
                        })}
                    </b>
                    <span className="cart-bonus-points-payment-box__points-max" data-testid="points-max">
                        {getPriceValueTranslation({ t, amount: MAX, currency: selectedCurrency })}
                    </span>
                </div>
                <Form.Slider name="bonusPoints" min={0} max={MAX} />
            </div>
            <div className="cart-bonus-points-payment-box__actions">
                <p data-testid="voucher-label">{t('CART_BONUS_POINTS_PAYMENT_BOX.VOUCHER_LABEL')}</p>
                <Button data-testid="voucher-apply-button">
                    {t('CART_BONUS_POINTS_PAYMENT_BOX.APPLY_BUTTON')}
                </Button>
            </div>
        </Card>
    );
};
